/**
 * Настройки сайта — разметка 1:1 с legacy htdocs/core/templates/admin_settings.tpl:
 * сообщения .bs-callout, form.form-settings с table.table.table-hover.table-bordered.table-condensed,
 * поле upd_value[id] у каждой строки, строка добавления (add_prog_name/add_rus_name/add_value)
 * и кнопка «сохранить изменения» (.btn.btn-xs.btn-info).
 *
 * Legacy отправлял одну POST-форму (settings-submit сохранял все значения сразу,
 * settings-delete[id] удалял строку); здесь то же самое через
 * PUT/POST/DELETE /api/admin/system/settings (Handlers\Admin\System, только суперадмин).
 */

import { useEffect, useState } from 'react'

import { api, ApiError } from '@/api/client'
import { useLegacyCss } from '@/lib/legacyAssets'
import { useApi } from '@/lib/useApi'

/** Строка таблицы settings. */
interface Setting {
  id: number
  prog_name: string
  rus_name: string
  value: string
}

export function AdminSettingsPage() {
  useLegacyCss('admin', 'newapproach', 'docs.min')

  const { data, reload } = useApi<Setting[]>('/api/admin/system/settings')

  const [values, setValues] = useState<Record<number, string>>({})
  const [add, setAdd] = useState({ prog_name: '', rus_name: '', value: '' })
  const [ok, setOk] = useState<string[]>([])
  const [error, setError] = useState<string[]>([])

  const settings = data ?? []

  useEffect(() => {
    const drafts: Record<number, string> = {}
    for (const setting of settings) drafts[setting.id] = setting.value ?? ''
    setValues(drafts)
  }, [data])

  /** legacy settings-submit: сохраняет значения всех строк и добавляет новую. */
  const submit = async (): Promise<void> => {
    try {
      for (const setting of settings) {
        const value = values[setting.id]
        if (value === undefined) continue
        await api.put(`/api/admin/system/settings/${setting.id}`, { value })
      }
      if (add.prog_name !== '' || add.rus_name !== '' || add.value !== '') {
        await api.post('/api/admin/system/settings', add)
        setAdd({ prog_name: '', rus_name: '', value: '' })
      }
      setOk(['Изменения существующих настроек сохранены.'])
      setError([])
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при сохранении.'])
      setOk([])
    }
  }

  /** legacy settings-delete[id]. */
  const remove = async (id: number): Promise<void> => {
    try {
      const result = await api.delete<{ message: string }>(`/api/admin/system/settings/${id}`)
      setOk([result.message])
      setError([])
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при удалении.'])
      setOk([])
    }
  }

  return (
    <>
      {error.length > 0 && (
        <div className="bs-callout bs-callout-danger">
          <h4>Ошибка</h4>
          {error.map((message, index) => (
            <p key={index}>{message}</p>
          ))}
        </div>
      )}
      {ok.length > 0 && (
        <div className="bs-callout bs-callout-info">
          <h4>OK</h4>
          {ok.map((message, index) => (
            <p key={index}>{message}</p>
          ))}
        </div>
      )}

      <form
        className="form-settings"
        method="POST"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <table className="table table-hover table-bordered table-condensed">
          <thead>
            <tr>
              <th>№</th>
              <th>Программное название</th>
              <th>Русское название</th>
              <th>Значение</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {settings.map((setting, index) => (
              <tr key={setting.id}>
                <td>{index + 1}</td>
                <td>{setting.prog_name}</td>
                <td>{setting.rus_name}</td>
                <td>
                  <input
                    className="form-control"
                    name={`upd_value[${setting.id}]`}
                    value={values[setting.id] ?? ''}
                    onChange={(e) => setValues({ ...values, [setting.id]: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="submit"
                    name={`settings-delete[${setting.id}]`}
                    className="btn btn-xs btn-danger"
                    value="удалить"
                    onClick={(e) => {
                      e.preventDefault()
                      void remove(setting.id)
                    }}
                  />
                </td>
              </tr>
            ))}
            <tr>
              <td />
              <td>
                <input
                  className="form-control"
                  name="add_prog_name"
                  value={add.prog_name}
                  onChange={(e) => setAdd({ ...add, prog_name: e.target.value })}
                />
              </td>
              <td>
                <input
                  className="form-control"
                  name="add_rus_name"
                  value={add.rus_name}
                  onChange={(e) => setAdd({ ...add, rus_name: e.target.value })}
                />
              </td>
              <td>
                <input
                  className="form-control"
                  name="add_value"
                  value={add.value}
                  onChange={(e) => setAdd({ ...add, value: e.target.value })}
                />
              </td>
              <td />
            </tr>
          </tbody>
        </table>
        <input
          type="submit"
          name="settings-submit"
          className="btn btn-xs btn-info"
          value="сохранить изменения"
        />
      </form>

      <TelegramWebhook />
      <MaxWebhook />
      <PlatesSync />
      <AutopaySweep />
    </>
  )
}

/** Состояние вебхука + флаги конфига (сами секреты бекенд наружу не отдаёт). */
interface WebhookState {
  telegram: {
    ok: boolean
    error?: string
    webhook?: {
      url: string
      pending_update_count: number
      last_error_message?: string
      last_error_date?: number
    }
  }
  config: {
    bot_username: string
    token_set: boolean
    secret_set: boolean
    webhook_url: string
    api_url: string
    channel_enabled: boolean
  }
}

/**
 * Вебхук бота уведомлений — управление из браузера.
 *
 * Отдельный блок здесь, а не CLI-скрипт: на проде нет доступа к консоли. Логика на
 * бекенде одна и та же (Services\TelegramNotify), консольный скрипт зовёт её же.
 *
 * Самое важное в этом блоке — last_error_message от Telegram. Именно там видно, ПОЧЕМУ
 * привязки не работают: «403 Forbidden» — не сошёлся секрет; «Wrong response from the
 * webhook» — по нашему адресу отвечает не обработчик, а SPA-заглушка (так бывает, если
 * вебхук зарегистрировали до выкатки кода).
 */
function TelegramWebhook() {
  const { data, reload, error: loadError, loading } = useApi<WebhookState>('/api/admin/telegram/webhook')

  const [busy, setBusy] = useState(false)
  const [ok, setOk] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const act = async (action: 'set' | 'delete'): Promise<void> => {
    setBusy(true)
    setOk(null)
    setError(null)
    try {
      const result =
        action === 'set'
          ? await api.post<{ message: string }>('/api/admin/telegram/webhook')
          : await api.delete<{ message: string }>('/api/admin/telegram/webhook')
      setOk(result.message)
      reload()
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Не удалось выполнить действие')
    } finally {
      setBusy(false)
    }
  }

  // РАНЬШЕ здесь стояло `if (data === null) return null` — и блок МОЛЧА исчезал со страницы
  // «Настройки», если запрос состояния вебхука не прошёл (не суперадмин, бэкенд без этого
  // роута при частичном деплое, сеть). Из-за этого «настройка телеграма пропадала». Теперь
  // не прячемся, а показываем, что грузится/почему не загрузилось.
  if (data === null) {
    return (
      <div style={{ marginTop: 30 }}>
        <h3>Telegram-бот уведомлений</h3>
        {loading ? (
          <p className="text-muted">Загрузка состояния вебхука…</p>
        ) : (
          <div className="alert alert-warning">
            Не удалось загрузить состояние вебхука{loadError !== null ? `: ${loadError}` : ''}. Проверьте,
            что вы вошли суперадмином и бэкенд обновлён (эндпоинт /api/admin/telegram/webhook).
          </div>
        )}
      </div>
    )
  }

  const hook = data.telegram.webhook
  const config = data.config
  const ready = config.token_set && config.secret_set && config.webhook_url !== ''

  return (
    <div style={{ marginTop: 30 }}>
      <h3>Telegram-бот уведомлений</h3>

      {error !== null && <div className="alert alert-danger">{error}</div>}
      {ok !== null && <div className="alert alert-success">{ok}</div>}

      <table className="table table-bordered">
        <tbody>
          <tr>
            <td style={{ width: 260 }}>Бот</td>
            <td>{config.bot_username !== '' ? `@${config.bot_username}` : <span className="text-muted">имя не определено</span>}</td>
          </tr>
          <tr>
            <td>Сервер Bot API</td>
            <td>{config.api_url}</td>
          </tr>
          <tr>
            <td>Токен бота</td>
            <td>
              {config.token_set ? (
                <span className="label label-success">задан</span>
              ) : (
                <span className="label label-danger">НЕ задан</span>
              )}
            </td>
          </tr>
          <tr>
            <td>Секрет вебхука</td>
            <td>
              {config.secret_set ? (
                <span className="label label-success">задан</span>
              ) : (
                <>
                  <span className="label label-danger">НЕ задан</span>{' '}
                  <span className="text-muted">
                    без него вебхук отвергает всё, и привязки будут молча теряться
                  </span>
                </>
              )}
            </td>
          </tr>
          <tr>
            <td>Канал telegram в рассылках</td>
            <td>
              {config.channel_enabled ? (
                <span className="label label-success">включён</span>
              ) : (
                <>
                  <span className="label label-warning">выключен</span>{' '}
                  <span className="text-muted">сообщения жителям не уходят (outbound.telegram)</span>
                </>
              )}
            </td>
          </tr>
          <tr>
            <td>Адрес вебхука в конфиге</td>
            <td>{config.webhook_url !== '' ? config.webhook_url : <span className="text-muted">не задан</span>}</td>
          </tr>
          <tr>
            <td>Вебхук зарегистрирован</td>
            <td>
              {data.telegram.ok !== true && (
                <span className="text-muted">{data.telegram.error ?? 'Telegram не ответил'}</span>
              )}
              {hook !== undefined && hook.url !== '' && (
                <>
                  <span className="label label-success">да</span> {hook.url}
                </>
              )}
              {hook !== undefined && hook.url === '' && <span className="label label-default">нет</span>}
            </td>
          </tr>
          {hook !== undefined && (
            <>
              <tr>
                <td>Апдейтов в очереди</td>
                <td>{hook.pending_update_count}</td>
              </tr>
              {hook.last_error_message !== undefined && hook.last_error_message !== '' && (
                <tr>
                  <td>Последняя ошибка Telegram</td>
                  <td style={{ color: '#ce4844' }}>{hook.last_error_message}</td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>

      <button
        type="button"
        className="btn btn-success"
        disabled={busy || !ready}
        onClick={() => void act('set')}
      >
        Зарегистрировать вебхук
      </button>{' '}
      <button
        type="button"
        className="btn btn-default"
        disabled={busy}
        onClick={() => void act('delete')}
      >
        Снять вебхук
      </button>

      {!ready && (
        <p className="text-muted" style={{ marginTop: 8 }}>
          <small>
            Пока не заданы токен бота, секрет и адрес вебхука в конфиге, регистрировать нечего.
          </small>
        </p>
      )}
      <p className="text-muted" style={{ marginTop: 8 }}>
        <small>
          Адрес и секрет берутся только из конфига и не передаются из браузера: иначе админку
          можно было бы уговорить перенаправить апдейты бота на чужой сервер.
        </small>
      </p>
    </div>
  )
}

/** Отчёт прогона автооплаты (GET/POST /api/admin/utils/autopay-run). */
interface AutopayReport {
  applied: boolean
  areas_total: number
  with_debt: number
  all_paid: number
  partial: number
  items: { id: number; name: string; unpaid: number; money: string; status: string }[]
}

/**
 * Ручной прогон автооплаты по всем участкам с включённой автооплатой.
 *
 * Зачем кнопка: «Обновить квитанции» создаёт счета, но автосписание НЕ запускает (его гоняет
 * только месячный крон). Если счета создали формой, долги у автооплатных участков висят —
 * эта кнопка догоняет то, что сделал бы крон. Логика на бекенде одна (Billing::sweepAll),
 * тот же прогон доступен консольным bin/run_autopay.php.
 *
 * «Проверить» — сухой прогон (кого затронет, без списания). «Списать у всех» — реально
 * двигает деньги (t_transactions + t_area.money), поэтому с подтверждением и только суперадмину.
 */
function AutopaySweep() {
  const [report, setReport] = useState<AutopayReport | null>(null)
  const [busy, setBusy] = useState(false)
  const [ok, setOk] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const check = async (): Promise<void> => {
    setBusy(true)
    setOk(null)
    setError(null)
    try {
      setReport(await api.get<AutopayReport>('/api/admin/utils/autopay-run'))
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Не удалось выполнить проверку')
    } finally {
      setBusy(false)
    }
  }

  const run = async (): Promise<void> => {
    if (
      !window.confirm(
        'Списать по всем участкам с включённой автооплатой?\n' +
          'Будут погашены неоплаченные счета за счёт баланса участков (реальное движение денег).',
      )
    ) {
      return
    }
    setBusy(true)
    setOk(null)
    setError(null)
    try {
      const result = await api.post<AutopayReport>('/api/admin/utils/autopay-run')
      setReport(result)
      setOk(`Готово. Погашено полностью: ${result.all_paid}, частично: ${result.partial}.`)
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Не удалось выполнить списание')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ marginTop: 30 }}>
      <h3>Автооплата по всем участкам</h3>

      {error !== null && <div className="alert alert-danger">{error}</div>}
      {ok !== null && <div className="alert alert-success">{ok}</div>}

      {report !== null && (
        <>
          <table className="table table-bordered">
            <tbody>
              <tr>
                <td style={{ width: 260 }}>Участков с автооплатой</td>
                <td>{report.areas_total}</td>
              </tr>
              <tr>
                <td>Из них с неоплаченными счетами</td>
                <td>
                  {report.with_debt === 0 ? (
                    <span className="label label-success">нет</span>
                  ) : (
                    <span className="label label-warning">{report.with_debt}</span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          {report.items.length > 0 && (
            <table className="table table-bordered table-condensed">
              <thead>
                <tr>
                  <th>Участок</th>
                  <th>Неоплаченных</th>
                  <th>Баланс</th>
                  {report.applied && <th>Итог</th>}
                </tr>
              </thead>
              <tbody>
                {report.items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td>{item.unpaid}</td>
                    <td>{item.money}</td>
                    {report.applied && (
                      <td>
                        {item.status === 'paid' ? (
                          <span className="label label-success">погашено</span>
                        ) : (
                          <span className="label label-warning">частично</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      <button type="button" className="btn btn-info" disabled={busy} onClick={() => void check()}>
        Проверить
      </button>{' '}
      <button
        type="button"
        className="btn btn-warning"
        disabled={busy || report === null}
        onClick={() => void run()}
      >
        Списать у всех
      </button>

      <p className="text-muted" style={{ marginTop: 8 }}>
        <small>
          «Проверить» только показывает, у кого есть долги. «Списать у всех» гасит неоплаченные
          счета за счёт баланса участков (в минус не уводит). Сначала проверьте, потом списывайте.
        </small>
      </p>
    </div>
  )
}

/** Отчёт сверки автономеров (GET /api/admin/plates/sync). */
interface PlatesReport {
  db_count: number
  api_count: number
  matched_count: number
  /** В БД, но нет в API — не зарегистрированы на шлагбауме (машину не пустит). */
  missing_in_api: { number: string; house: number; owner: string; db_id: number }[]
  /** В API, но нет в БД — лишние на шлагбауме (пустит чужого). */
  extra_in_api: { number: string; api_id: string; owner_name: string }[]
}

/** Результат синхронизации (POST /api/admin/plates/sync). */
interface PlatesApplyResult {
  report: PlatesReport
  added: { number: string; response: string }[]
  deleted: string[]
}

/**
 * Сверка и синхронизация автономеров с системой распознавания (шлагбаум).
 *
 * Блок здесь, а не отдельный CLI-скрипт: на проде нет доступа к консоли. Логика на
 * бекенде одна (Services\PlatesSync), консольный bin/sync_plates_check.php зовёт её же.
 *
 * Истина — наша БД (t_avtoplates). Шлагбаум (airnet NR API) должен содержать РОВНО
 * активные номера участков. «Сверить» только показывает расхождения (ничего не меняет),
 * «Синхронизировать» приводит шлагбаум к состоянию БД: регистрирует недостающие и
 * снимает лишние. Отдельно проверять на загрузке страницы НЕ ходим в airnet — только
 * по кнопке, чтобы не дёргать внешнюю систему на каждом заходе в «Настройки».
 */
function PlatesSync() {
  const [report, setReport] = useState<PlatesReport | null>(null)
  const [applied, setApplied] = useState<PlatesApplyResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [ok, setOk] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const check = async (): Promise<void> => {
    setBusy(true)
    setOk(null)
    setError(null)
    setApplied(null)
    try {
      const result = await api.get<PlatesReport>('/api/admin/plates/sync')
      setReport(result)
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Не удалось выполнить сверку')
    } finally {
      setBusy(false)
    }
  }

  const sync = async (): Promise<void> => {
    if (
      !window.confirm(
        'Синхронизировать шлагбаум с базой?\n' +
          'Недостающие номера будут ЗАРЕГИСТРИРОВАНЫ, лишние — СНЯТЫ на реальной системе распознавания.',
      )
    ) {
      return
    }
    setBusy(true)
    setOk(null)
    setError(null)
    try {
      const result = await api.post<PlatesApplyResult>('/api/admin/plates/sync')
      setApplied(result)
      setReport(result.report)
      setOk(`Зарегистрировано: ${result.added.length}, снято: ${result.deleted.length}.`)
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Не удалось синхронизировать')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ marginTop: 30 }}>
      <h3>Автономера ↔ шлагбаум (распознавание)</h3>

      {error !== null && <div className="alert alert-danger">{error}</div>}
      {ok !== null && <div className="alert alert-success">{ok}</div>}

      {report !== null && (
        <>
          <table className="table table-bordered">
            <tbody>
              <tr>
                <td style={{ width: 260 }}>Активных номеров в базе</td>
                <td>{report.db_count}</td>
              </tr>
              <tr>
                <td>Зарегистрировано на шлагбауме</td>
                <td>{report.api_count}</td>
              </tr>
              <tr>
                <td>Совпало</td>
                <td>
                  <span className="label label-success">{report.matched_count}</span>
                </td>
              </tr>
            </tbody>
          </table>

          <p>
            <b>В базе, но НЕТ на шлагбауме</b> (машину не пустит):{' '}
            {report.missing_in_api.length === 0 ? (
              <span className="label label-success">нет</span>
            ) : (
              <span className="label label-warning">{report.missing_in_api.length}</span>
            )}
          </p>
          {report.missing_in_api.length > 0 && (
            <table className="table table-bordered table-condensed">
              <thead>
                <tr>
                  <th>Номер</th>
                  <th>Участок</th>
                  <th>Владелец</th>
                </tr>
              </thead>
              <tbody>
                {report.missing_in_api.map((item) => (
                  <tr key={item.db_id}>
                    <td>{item.number}</td>
                    <td>{item.house}</td>
                    <td>{item.owner}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <p>
            <b>На шлагбауме, но НЕТ в базе</b> (пустит чужого):{' '}
            {report.extra_in_api.length === 0 ? (
              <span className="label label-success">нет</span>
            ) : (
              <span className="label label-danger">{report.extra_in_api.length}</span>
            )}
          </p>
          {report.extra_in_api.length > 0 && (
            <table className="table table-bordered table-condensed">
              <thead>
                <tr>
                  <th>Номер</th>
                  <th>Владелец (по данным шлагбаума)</th>
                </tr>
              </thead>
              <tbody>
                {report.extra_in_api.map((item) => (
                  <tr key={item.api_id + item.number}>
                    <td>{item.number}</td>
                    <td>{item.owner_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {applied !== null && (applied.added.length > 0 || applied.deleted.length > 0) && (
        <div className="bs-callout bs-callout-info">
          <h4>Синхронизация выполнена</h4>
          {applied.added.length > 0 && (
            <p>Зарегистрированы: {applied.added.map((a) => a.number).join(', ')}</p>
          )}
          {applied.deleted.length > 0 && <p>Сняты: {applied.deleted.join(', ')}</p>}
        </div>
      )}

      <button type="button" className="btn btn-info" disabled={busy} onClick={() => void check()}>
        Сверить
      </button>{' '}
      <button
        type="button"
        className="btn btn-warning"
        disabled={busy || report === null}
        onClick={() => void sync()}
      >
        Синхронизировать
      </button>

      <p className="text-muted" style={{ marginTop: 8 }}>
        <small>
          «Сверить» только показывает расхождения. «Синхронизировать» приводит шлагбаум к базе:
          регистрирует недостающие номера и снимает лишние на реальной системе распознавания.
          Сначала сверьте, потом синхронизируйте.
        </small>
      </p>
    </div>
  )
}

/** Подписка бота MAX на апдейты (GET /api/admin/max/webhook). */
interface MaxWebhookState {
  max: {
    ok: boolean
    error?: string
    subscriptions?: { url: string; time: number; update_types?: string[] }[]
  }
  config: {
    bot_username: string
    token_set: boolean
    secret_set: boolean
    webhook_url: string
    api_url: string
    channel_enabled: boolean
  }
}

/**
 * Вебхук бота MAX — управление из браузера, как и у Telegram (на проде нет консоли).
 *
 * Разница с Telegram, которую здесь важно понимать: MAX НЕ рассказывает, почему апдейты
 * не доходят — аналога last_error_message у него нет, он показывает только сам факт
 * подписки. Поэтому если привязка не работает, а подписка на месте, смотреть надо в
 * журнал рассылок (вкладка MAX) и в logs/outbound.log: отвергнутые вебхуки пишутся туда.
 */
function MaxWebhook() {
  const { data, reload, error: loadError, loading } = useApi<MaxWebhookState>('/api/admin/max/webhook')

  const [busy, setBusy] = useState(false)
  const [ok, setOk] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const act = async (action: 'set' | 'delete'): Promise<void> => {
    setBusy(true)
    setOk(null)
    setError(null)
    try {
      const result =
        action === 'set'
          ? await api.post<{ message: string }>('/api/admin/max/webhook')
          : await api.delete<{ message: string }>('/api/admin/max/webhook')
      setOk(result.message)
      reload()
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Не удалось выполнить действие')
    } finally {
      setBusy(false)
    }
  }

  // Как и у Telegram: не прячем блок молча при сбое запроса — показываем причину.
  if (data === null) {
    return (
      <div style={{ marginTop: 30 }}>
        <h3>MAX-бот уведомлений</h3>
        {loading ? (
          <p className="text-muted">Загрузка состояния вебхука…</p>
        ) : (
          <div className="alert alert-warning">
            Не удалось загрузить состояние подписки{loadError !== null ? `: ${loadError}` : ''}. Проверьте,
            что вы вошли суперадмином и бэкенд обновлён (эндпоинт /api/admin/max/webhook).
          </div>
        )}
      </div>
    )
  }

  const config = data.config
  const subs = data.max.subscriptions ?? []
  const ready = config.token_set && config.secret_set && config.webhook_url !== ''
  // Подписка есть, но НЕ на наш адрес — самая коварная поломка: вебхук вроде «стоит»,
  // а апдейты уходят на чужой сервер (например, оставшийся от прежнего стенда).
  const ours = subs.some((s) => s.url === config.webhook_url)

  return (
    <div style={{ marginTop: 30 }}>
      <h3>MAX-бот уведомлений</h3>

      {error !== null && <div className="alert alert-danger">{error}</div>}
      {ok !== null && <div className="alert alert-success">{ok}</div>}

      <table className="table table-bordered">
        <tbody>
          <tr>
            <td style={{ width: 260 }}>Бот</td>
            <td>
              {config.bot_username !== '' ? (
                `@${config.bot_username}`
              ) : (
                <span className="text-muted">имя не определено</span>
              )}
            </td>
          </tr>
          <tr>
            <td>Сервер Bot API</td>
            <td>{config.api_url}</td>
          </tr>
          <tr>
            <td>Токен бота</td>
            <td>
              {config.token_set ? (
                <span className="label label-success">задан</span>
              ) : (
                <span className="label label-danger">НЕ задан</span>
              )}
            </td>
          </tr>
          <tr>
            <td>Секрет вебхука</td>
            <td>
              {config.secret_set ? (
                <span className="label label-success">задан</span>
              ) : (
                <>
                  <span className="label label-danger">НЕ задан</span>{' '}
                  <span className="text-muted">
                    без него вебхук отвергает всё, и привязки будут молча теряться
                  </span>
                </>
              )}
            </td>
          </tr>
          <tr>
            <td>Канал MAX в рассылках</td>
            <td>
              {config.channel_enabled ? (
                <span className="label label-success">включён</span>
              ) : (
                <>
                  <span className="label label-warning">выключен</span>{' '}
                  <span className="text-muted">сообщения жителям не уходят (outbound.max)</span>
                </>
              )}
            </td>
          </tr>
          <tr>
            <td>Адрес вебхука в конфиге</td>
            <td>
              {config.webhook_url !== '' ? (
                config.webhook_url
              ) : (
                <span className="text-muted">не задан</span>
              )}
            </td>
          </tr>
          <tr>
            <td>Подписка у MAX</td>
            <td>
              {data.max.ok !== true && (
                <span className="text-muted">{data.max.error ?? 'MAX не ответил'}</span>
              )}
              {data.max.ok === true && subs.length === 0 && (
                <span className="label label-default">нет</span>
              )}
              {data.max.ok === true && subs.length > 0 && (
                <>
                  {ours ? (
                    <span className="label label-success">да</span>
                  ) : (
                    <span className="label label-danger">не на наш адрес</span>
                  )}
                  {subs.map((s) => (
                    <div key={s.url}>
                      {s.url}
                      {s.update_types !== undefined && s.update_types.length > 0 && (
                        <span className="text-muted"> — {s.update_types.join(', ')}</span>
                      )}
                    </div>
                  ))}
                </>
              )}
            </td>
          </tr>
        </tbody>
      </table>

      <button
        type="button"
        className="btn btn-success"
        disabled={busy || !ready}
        onClick={() => void act('set')}
      >
        Зарегистрировать вебхук
      </button>{' '}
      <button
        type="button"
        className="btn btn-default"
        disabled={busy}
        onClick={() => void act('delete')}
      >
        Снять вебхук
      </button>

      {!ready && (
        <p className="text-muted" style={{ marginTop: 8 }}>
          <small>
            Пока не заданы токен бота, секрет и адрес вебхука в конфиге, регистрировать нечего.
          </small>
        </p>
      )}
      <p className="text-muted" style={{ marginTop: 8 }}>
        <small>
          Секрет MAX принимает только из латиницы, цифр, «_» и «-» (5–256 символов).
          Адрес и секрет берутся только из конфига и не передаются из браузера.
        </small>
      </p>
    </div>
  )
}
